import { Download, Loader2, CheckCircle, XCircle } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useToast } from '@/components/ui/toast'
import {
  useExportWithPolling,
  getExportDownloadUrl,
  type ExportType,
  type ExportFormat,
  type ExportJob,
} from '@/hooks'
import { cn } from '@/lib/utils'

// ==================== Types ====================

interface ExportButtonProps {
  type: ExportType
  filters: Record<string, unknown>
  disabled?: boolean
  className?: string
}

// ==================== Component ====================

export function ExportButton({ type, filters, disabled, className }: ExportButtonProps) {
  const { success, error: showError } = useToast()
  const [completedJob, setCompletedJob] = useState<ExportJob | null>(null)

  const { startExport, reset, status, isCreating, isPolling } = useExportWithPolling({
    onSuccess: (job) => {
      setCompletedJob(job)
      success('导出完成', '文件已准备好，点击下载')
    },
    onError: (errorMsg) => {
      showError('导出失败', errorMsg)
    },
  })

  const handleExport = async (format: ExportFormat) => {
    setCompletedJob(null)
    await startExport({ type, filters, format })
  }

  const handleDownload = () => {
    if (completedJob) {
      window.open(getExportDownloadUrl(completedJob.id), '_blank')
      reset()
      setCompletedJob(null)
    }
  }

  const isLoading = isCreating || isPolling
  const isCompleted = status === 'completed' && completedJob
  const isFailed = status === 'failed'

  // 显示下载按钮
  if (isCompleted) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={handleDownload}
        className={cn('gap-2', className)}
      >
        <CheckCircle className="h-4 w-4 text-green-500" />
        下载文件
      </Button>
    )
  }

  // 显示失败状态
  if (isFailed) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={reset}
        className={cn('gap-2', className)}
      >
        <XCircle className="h-4 w-4 text-red-500" />
        重试
      </Button>
    )
  }

  // 显示加载状态
  if (isLoading) {
    return (
      <Button variant="outline" size="sm" disabled className={cn('gap-2', className)}>
        <Loader2 className="h-4 w-4 animate-spin" />
        {status === 'processing' ? '处理中...' : '准备中...'}
      </Button>
    )
  }

  // 显示导出按钮
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          className={cn('gap-2', className)}
        >
          <Download className="h-4 w-4" />
          导出
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => void handleExport('xlsx')}>
          导出为 Excel (.xlsx)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => void handleExport('csv')}>
          导出为 CSV (.csv)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
